'use client';

import { useState } from 'react';
import { X, Ruler } from 'lucide-react';

export interface SizeGuideData {
  nameEs: string;
  unit: string;
  rows: Record<string, string | number>[];
  notesEs: string | null;
}

interface Props {
  guide: SizeGuideData;
}

/**
 * Modal inline con la guía de tallas. Se abre desde un link en el accordion del
 * BuyBox. Renderiza una tabla a partir de `rows` (cada fila es un objeto con
 * claves arbitrarias; la primera clave se trata como etiqueta de la fila).
 */
export function SizeGuideModal({ guide }: Props) {
  const [open, setOpen] = useState(false);
  if (guide.rows.length === 0) return null;

  const columns = Array.from(
    guide.rows.reduce<Set<string>>((set, row) => {
      Object.keys(row).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  const [first, ...rest] = columns;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="type-caption inline-flex items-center gap-1 text-[var(--color-accent)] underline underline-offset-2 hover:text-[var(--color-accent)]"
      >
        <Ruler className="h-3 w-3" /> {guide.nameEs}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="size-guide-title"
        >
          <button
            type="button"
            aria-label="Cerrar"
            className="absolute inset-0 bg-[var(--color-bg)]/80"
            onClick={() => setOpen(false)}
          />
          <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-sm bg-[var(--color-surface)] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="size-guide-title" className="type-h3 text-[var(--color-fg)]">
                {guide.nameEs}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar guía"
                className="text-[var(--color-fg-muted)] hover:text-[var(--color-accent)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="type-caption mb-4 text-[var(--color-fg-subtle)]">
              Medidas en {guide.unit}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="border-b border-[var(--color-border)]">
                  <tr>
                    {first ? (
                      <th className="type-eyebrow py-2 pr-3 text-left text-[var(--color-fg-subtle)]">
                        {first}
                      </th>
                    ) : null}
                    {rest.map((c) => (
                      <th
                        key={c}
                        className="type-eyebrow py-2 pr-3 text-left text-[var(--color-fg-subtle)]"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {guide.rows.map((row, i) => (
                    <tr key={i} className="border-b border-[var(--color-border)]">
                      {first ? (
                        <td className="type-body-sm py-2 pr-3 font-mono text-[var(--color-fg)]">
                          {String(row[first] ?? '—')}
                        </td>
                      ) : null}
                      {rest.map((c) => (
                        <td key={c} className="type-body-sm py-2 pr-3 text-[var(--color-fg-muted)]">
                          {String(row[c] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {guide.notesEs ? (
              <p className="type-caption mt-4 text-[var(--color-fg-muted)]">{guide.notesEs}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
